import { useState } from 'react'
import { Settings, Plus } from 'lucide-react'
import { useDashboardData } from './use-dashboard-data'
import { useFavorites } from '../hooks/use-favorites'
import { HeuteBlock } from './heute-block'
import { BereichCard } from './bereich-card'
import { OverviewChart } from './overview-chart'
import { WelcomeView } from './welcome-view'
import { CreateBereichSheet } from '../bereiche/create-bereich-sheet.jsx'

export function Dashboard({ authToken, userStores, landingData, activeSessions, onOpenSettings, onSelectStore, onCreateStore, onJoinStore, dark }) {
  const { stores } = useDashboardData(authToken, userStores)
  const { isFav } = useFavorites()
  const [createOpen, setCreateOpen] = useState(false)
  const [createInitialType, setCreateInitialType] = useState(null)
  const [createInitialStep, setCreateInitialStep] = useState(null)

  // Welcome-/Create-Karten: 'join' öffnet den Beitreten-Step, sonst einen Channel.
  const openCreate = (channel) => {
    if (channel === 'join') {
      setCreateInitialType(null)
      setCreateInitialStep('join')
    } else {
      setCreateInitialType(channel)
      setCreateInitialStep(null)
    }
    setCreateOpen(true)
  }

  if (stores.length === 0) {
    return (
      <>
        <WelcomeView onSelectChannel={openCreate} onOpenSettings={onOpenSettings} />
        <CreateBereichSheet
          open={createOpen}
          onOpenChange={setCreateOpen}
          userStores={userStores}
          onCreateStore={onCreateStore}
          onJoinStore={onJoinStore}
          dark={dark}
          initialType={createInitialType}
          initialStep={createInitialStep}
        />
      </>
    )
  }

  const favStores = stores.filter(s => isFav(s.id))
  const showInBereiche = favStores.length > 0 ? favStores : stores

  // Reiner Helfer: mind. 1 Store und ALLE sind Helfer-Stores. Wer einen eigenen
  // Store hat (und woanders hilft), ist KEIN reiner Helfer → volles Owner-Dashboard.
  const isPureHelper = stores.length > 0 && stores.every(s => s.role === 'fulfillment')

  // Heute-Zustand aus echten Daten ableiten.
  const byStore = Object.values(landingData?.byStore ?? {})
  const onlineData = byStore.filter(s => s.store?.type === 'online')
  const openOrdersCount = onlineData.reduce((n, s) => n + (s.openOrdersCount ?? 0), 0)
  const openOrders = onlineData.flatMap(s => s.openOrdersFirst2 ?? []).slice(0, 2)

  // Helfer-Modus: Versand-Queue ist "Bereit" — Count und Vorschau aus derselben
  // Quelle (ordersReady / readyOrdersFirst2), explizit auf type === 'online' gefiltert.
  const readyCount = onlineData.reduce((n, s) => n + (s.ordersReady ?? 0), 0)
  const readyOrders = onlineData.flatMap(s => s.readyOrdersFirst2 ?? []).slice(0, 2)

  // Was der Heute-Block zeigt — Helfer sieht Bereit-Queue, Owner die offenen Bestellungen.
  const heuteCount = isPureHelper ? readyCount : openOrdersCount
  const heuteOrders = isPureHelper ? readyOrders : openOrders

  // Aktive Live-Session: bevorzugt aus landingData, sonst aus activeSessions-Map.
  let liveSession = null
  for (const s of byStore) {
    if (s.store?.type === 'flea' && s.liveSession) {
      liveSession = { ...s.liveSession, storeId: s.store.id }
      break
    }
  }
  if (!liveSession) {
    for (const [storeId, sess] of Object.entries(activeSessions || {})) {
      if (sess && !sess.ends_at) {
        liveSession = { storeId, location: sess.location || '', starts_at: sess.starts_at, todayRevenue: 0, salesCount: null }
        break
      }
    }
  }

  const openStoreOfType = (type) => {
    const st = stores.find(s => s.type === type)
    if (st && onSelectStore) onSelectStore(st)
  }
  const openLive = () => {
    const st = stores.find(s => s.id === liveSession?.storeId) || stores.find(s => s.type === 'flea')
    if (st && onSelectStore) onSelectStore(st, { openLiveDirectly: true })
  }

  return (
   <>
    <div data-testid="screen-area-picker" className="px-[18px]" style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}>
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md overflow-hidden">
            <img src="/boothly-logo.png" alt="Boothly" className="w-full h-full object-cover" />
          </div>
          <span className="font-medium text-base">Boothly</span>
        </div>
        <button onClick={onOpenSettings} data-testid="settings-button" aria-label="Einstellungen" className="-mr-3 flex h-11 w-11 items-center justify-center rounded-full text-[var(--label-secondary)] active:bg-[var(--fill-quaternary)]">
          <Settings size={21} />
        </button>
      </div>

      <div className="mt-3 mb-5">
        <h3 className="text-[22px] font-medium">Willkommen zurück</h3>
        <p className="text-[13px] text-muted-foreground mt-1">{greetingSubFor({ liveSession, openOrdersCount: heuteCount, isPureHelper })}</p>
      </div>

      <HeuteBlock
        liveSession={liveSession}
        openOrders={heuteOrders}
        openOrdersCount={heuteCount}
        onOpenStore={openStoreOfType}
        onOpenLive={openLive}
      />

      {/* Chart nur für Owner — fulfillment darf serverseitig keine Stats sehen. */}
      {!isPureHelper && (
        <div className="mt-5">
          <OverviewChart data={landingData?.weeks ?? []} />
        </div>
      )}

      <div className="text-[15px] font-medium mt-5 mb-3">Meine Bereiche</div>
      {showInBereiche.map(s => (
        <BereichCard
          key={s.id}
          store={s}
          storeData={landingData?.byStore?.[s.id]}
          onClick={() => onSelectStore && onSelectStore(s)}
        />
      ))}

      {/* Bereich anlegen — auch für reine Helfer sichtbar (eigenen Bereich starten). */}
      <button
        onClick={() => openCreate(null)}
        data-testid="area-create-button"
        className="mt-[11px] w-full border border-[var(--border-default)] rounded-[16px] p-[14px] flex items-center justify-center gap-[9px] active:scale-[0.99] transition-transform"
      >
        <Plus size={19} style={{ color: 'var(--accent-troedel)' }} />
        <span className="text-sm font-medium text-[var(--label-secondary)]">Bereich anlegen</span>
      </button>
    </div>

    <CreateBereichSheet
      open={createOpen}
      onOpenChange={setCreateOpen}
      userStores={userStores}
      onCreateStore={onCreateStore}
      onJoinStore={onJoinStore}
      dark={dark}
      initialType={createInitialType}
      initialStep={createInitialStep}
    />
   </>
  )
}

function greetingSubFor({ liveSession, openOrdersCount, isPureHelper }) {
  if (isPureHelper) return openOrdersCount > 0 ? 'Pakete warten auf Versand.' : 'Alles versendet — nichts zu tun.'
  if (liveSession) return 'Dein Trödelmarkt läuft.'
  if (openOrdersCount > 0) return 'Hier ist dein Überblick.'
  return 'Alles im grünen Bereich.'
}

